import { useMemo, useRef, useState } from 'react';

import { api } from '@/app/api';
import { UiButton, UiInput, UiNativeSelect } from '@shared/common/ui';

import { ProfilePendingReceiptsWidget } from './ProfilePendingReceiptsWidget';

/**
 * Виджет «Оборот» по данным PayKeeper (ТЗ «Виджет Оборот»).
 *
 * Ничего не запрашивает при открытии страницы: тяжёлый обход реестра чеков
 * стартует только по кнопке. Смена периода не считает заново, а обесценивает
 * прошлый результат (§10, §16).
 */

const turnoverFormatter = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const integerFormatter = new Intl.NumberFormat('ru-RU');

const dateFormatter = new Intl.DateTimeFormat('ru-RU');

const dateTimeFormatter = new Intl.DateTimeFormat('ru-RU', {
  dateStyle: 'short',
  timeStyle: 'short',
});

const PERIOD_OPTIONS = [
  { id: 'year', label: 'Текущий год' },
  { id: 'month', label: 'Текущий месяц' },
  { id: 'today', label: 'Сегодня' },
  { id: 'custom', label: 'Произвольный период' },
];

function toIsoDate(date) {
  const shifted = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return shifted.toISOString().slice(0, 10);
}

function presetRange(preset) {
  const now = new Date();
  const today = toIsoDate(now);
  if (preset === 'today') return { dateFrom: today, dateTo: today };
  if (preset === 'month') {
    return { dateFrom: toIsoDate(new Date(now.getFullYear(), now.getMonth(), 1)), dateTo: today };
  }
  return { dateFrom: `${now.getFullYear()}-01-01`, dateTo: today };
}

function formatMoney(value) {
  return turnoverFormatter.format(Number(value) || 0);
}

function formatDate(value) {
  return value ? dateFormatter.format(new Date(`${value}T00:00:00`)) : '—';
}

export function ProfileTurnoverWidget() {
  const [preset, setPreset] = useState('year');
  const [customRange, setCustomRange] = useState(() => presetRange('year'));
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const requestId = useRef(0);

  const range = useMemo(
    () => (preset === 'custom' ? customRange : presetRange(preset)),
    [preset, customRange],
  );

  // Любая правка периода делает прошлый расчёт неактуальным (§10).
  const dropResult = () => {
    requestId.current += 1;
    setResult(null);
    setError(null);
    setDetailsOpen(false);
    setLoading(false);
  };

  const onPresetChange = (next) => {
    setPreset(next);
    if (next !== 'custom') setCustomRange(presetRange(next));
    dropResult();
  };

  const onCustomChange = (patch) => {
    setCustomRange((current) => ({ ...current, ...patch }));
    dropResult();
  };

  const calculate = async () => {
    if (loading) return;
    if (!range.dateFrom || !range.dateTo) {
      setError('Укажите обе даты периода');
      return;
    }
    if (range.dateFrom > range.dateTo) {
      setError('Дата начала не может быть позже даты окончания');
      return;
    }
    setLoading(true);
    setError(null);
    const currentRequest = ++requestId.current;
    try {
      const { data } = await api.post('/owner/finance/turnover', range);
      if (currentRequest === requestId.current) setResult(data.turnover);
    } catch (err) {
      if (currentRequest !== requestId.current) return;
      // Частичный результат не показываем: он выглядел бы как настоящий (§14).
      setResult(null);
      setError(err.response?.data?.message ?? 'Не удалось получить данные PayKeeper.');
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  };

  const showPsn = preset === 'year' && Boolean(result?.psnEnabled) && Boolean(result?.psnLimits?.length);

  return (
    <div className="form-card form-card--wide profile-turnover">
      <div className="profile-turnover__overview">
        <div className="profile-turnover__header">
          <h2>Оборот</h2>
          <p className="muted">По чекам PayKeeper</p>
        </div>

        <div className="profile-turnover__period">
          <UiNativeSelect
            label="Период"
            value={preset}
            onChange={(event) => onPresetChange(event.target.value)}
          >
            {PERIOD_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </UiNativeSelect>

          {preset === 'custom' && (
            <div className="profile-turnover__dates">
              <UiInput
                label="С"
                type="date"
                value={customRange.dateFrom}
                max={customRange.dateTo || undefined}
                onChange={(event) => onCustomChange({ dateFrom: event.target.value })}
              />
              <UiInput
                label="По"
                type="date"
                value={customRange.dateTo}
                min={customRange.dateFrom || undefined}
                onChange={(event) => onCustomChange({ dateTo: event.target.value })}
              />
            </div>
          )}
        </div>

        {result ? (
          <>
            <p className="profile-turnover__range">
              {formatDate(result.dateFrom)} — {formatDate(result.dateTo)}
            </p>

            <div className="profile-turnover__total">
              <strong>{formatMoney(result.turnover)}</strong>
              <span>Чистый оборот</span>
            </div>

            <dl className="profile-turnover__rows">
              <div>
                <dt>Получено</dt>
                <dd>{formatMoney(result.income)}</dd>
              </div>
              <div>
                <dt>Возвраты</dt>
                <dd className="profile-turnover__value--negative">
                  {result.refunds > 0 ? `−${formatMoney(result.refunds)}` : formatMoney(0)}
                </dd>
              </div>
              <div>
                <dt>Наличные</dt>
                <dd>{formatMoney(result.cash)}</dd>
              </div>
              <div>
                <dt>Безналичные</dt>
                <dd>{formatMoney(result.cashless)}</dd>
              </div>
              <div>
                <dt>Чеков прихода</dt>
                <dd>{integerFormatter.format(result.saleReceiptsCount)}</dd>
              </div>
              <div>
                <dt>Чеков возврата</dt>
                <dd>{integerFormatter.format(result.refundReceiptsCount)}</dd>
              </div>
            </dl>

            {detailsOpen && (
              <dl className="profile-turnover__rows profile-turnover__rows--details">
                <div>
                  <dt>Чеков коррекции</dt>
                  <dd>{integerFormatter.format(result.correctionReceiptsCount)}</dd>
                </div>
                <div>
                  <dt>Проблемных чеков</dt>
                  <dd>{integerFormatter.format(result.problemReceiptsCount)}</dd>
                </div>
                <div>
                  <dt>Сумма проблемных чеков</dt>
                  <dd>{formatMoney(result.problemAmount)}</dd>
                </div>
                <div>
                  <dt>Всего чеков в реестре</dt>
                  <dd>{integerFormatter.format(result.receiptsProcessed)}</dd>
                </div>
                <div>
                  <dt title="Зачёт ранее полученных предоплат — в оборот не входит">
                    Зачтено предоплат
                  </dt>
                  <dd>{formatMoney(result.advanceOffset)}</dd>
                </div>
              </dl>
            )}

            {showPsn && (
              <div className="profile-turnover__psn">
                <h3>ПСН</h3>
                {result.psnLimits.map((limit) => {
                  const left = Math.max(limit - result.turnover, 0);
                  const percent = limit > 0 ? Math.min((result.turnover / limit) * 100, 100) : 0;
                  return (
                    <div className="profile-turnover__psn-item" key={limit}>
                      <div className="profile-turnover__psn-row">
                        <span>До {formatMoney(limit)}</span>
                        <span>осталось {formatMoney(left)}</span>
                      </div>
                      <div className="profile-turnover__bar">
                        <div
                          className={`profile-turnover__bar-fill${percent >= 90 ? ' profile-turnover__bar-fill--danger' : ''}`}
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <small>{Math.round(percent)}%</small>
                    </div>
                  );
                })}
              </div>
            )}

            <p
              className="profile-turnover__note"
              title="Если ИП получает доход, который не проходит через PayKeeper, такой доход в данном расчёте не учитывается."
            >
              Расчёт выполнен только по данным PayKeeper.
            </p>

            <p className="profile-turnover__calculated">
              Получено из PayKeeper {dateTimeFormatter.format(new Date(result.calculatedAt))}
            </p>
          </>
        ) : (
          <p className="profile-turnover__empty">
            {loading
              ? 'Получаем данные из PayKeeper…'
              : error
                ? 'Для выбранного периода расчёт не выполнен.'
                : 'Для выбранного периода данные ещё не рассчитаны.'}
          </p>
        )}

        {error && <div className="form-note form-note--error">{error}</div>}

        <div className="profile-turnover__actions">
          <UiButton type="button" disabled={loading} onClick={calculate}>
            {loading
              ? 'Получаем данные…'
              : error
                ? 'Попробовать снова'
                : result
                  ? 'Рассчитать заново'
                  : 'Рассчитать оборот'}
          </UiButton>
          {result && (
            <UiButton
              type="button"
              variant="ghost"
              onClick={() => setDetailsOpen((current) => !current)}
            >
              {detailsOpen ? 'Свернуть' : 'Подробнее'}
            </UiButton>
          )}
        </div>
      </div>
      <ProfilePendingReceiptsWidget result={result} loading={loading} error={error} />
    </div>
  );
}
